import React, {cloneElement, isValidElement, ReactNode, useCallback, useState} from 'react';
import cx from 'classnames';
import {Text, Icon, PriorityIcon, Popover} from '@priority-software/priority-style-react';
import {isRTL} from '../../../../Localization';
import {HyperlinkText} from '../../../../components/Common/LinkTexts/LinkTexts';
import {getFieldHelp} from '../../service/api/general';
import styles from './style.module.scss';
import {createDataTestId} from '../../../../utils/dataTestId';
import {cleanContent} from '../../../../utils/cleanHTMLContent';

type Props = {
    hasTooltip?: boolean;
    label?: ReactNode;
    required?: boolean;
    componentClasses?: string;
    error?: string;
    readOnly?: boolean;
    readOnlyValue?: string;
    children?: ReactNode;
    description?: ReactNode;
    fieldName: string;
    formName?: string;
    onHyperLinkClick: (linkData: {eName: string}) => void;
};

export const ControlWrapper = ({
    componentClasses = '',
    hasTooltip,
    label,
    required,
    children,
    readOnly,
    readOnlyValue,
    error,
    description,
    fieldName = '',
    formName,
    onHyperLinkClick
}: Props) => {
    const [popoverData, setPopoverDesc] = useState(null);
    const [open, setOpen] = useState(false);

    const onPopoverLinkClick = useCallback(
        params => {
            setOpen(false);
            onHyperLinkClick(params);
        },
        [onHyperLinkClick, setOpen]
    );

    const openPopover = useCallback(() => {
        setOpen(true);
    }, [setOpen]);

    const closePopover = useCallback(() => {
        setOpen(false);
    }, [setOpen]);

    const requestFieldHelp = useCallback(async () => {
        if (!popoverData) {
            try {
                const res = await getFieldHelp(formName, fieldName);
                const msgData = res.Messages[0];

                if (msgData) {
                    const message = msgData.Text;
                    const link = msgData.Links;
                    const data = message.split('\n').filter(t => t.length);
                    const popoverTitle = data[0];
                    const text = data.length > 1 ? data.join('\n') : data.toString();
                    const popoverText = cleanContent(text, true);

                    const linkData = res
                        ? Object.entries(link).map(([key, value]) => ({
                              from: popoverText.indexOf(key),
                              len: key.length,
                              title: value,
                              linkData: {
                                  eName: key.replace(/[{}]/g, '').split('.')[0]
                              }
                          }))
                        : [];

                    setPopoverDesc({
                        title: popoverTitle,
                        text: popoverText,
                        linkData
                    });
                }

                openPopover();
            } catch (e) {
                console.error(e);
            }
        } else {
            openPopover();
        }
    }, [popoverData, setPopoverDesc, openPopover, fieldName, formName]);

    return (
        <div className={cx(styles.component, componentClasses)}>
            <div className={styles.bottomMargin}>
                <Text
                    componentClasses={styles.label}
                    tag="label"
                    skin={'label-1'}
                    textColor={'primary-700'}
                >
                    <span className={styles.text}>{label}</span>

                    {required ? (
                        <Text
                            tag="span"
                            textColor="validation-red"
                            skin="subtitle-1"
                            componentClasses={styles.asterisk}
                        >
                            *
                        </Text>
                    ) : null}

                    {hasTooltip ? (
                        <Popover
                            {...createDataTestId('popover-control-container', true)}
                            escClosable
                            show={open}
                            rtl={isRTL()}
                            componentClasses={styles.fieldTooltip}
                            icon={PriorityIcon.questionCircle}
                            title={
                                <div {...createDataTestId('popover-control-title', false)}>
                                    {label}
                                </div>
                            }
                            content={
                                <HyperlinkText
                                    componentClasses={cx(styles.popoverText, styles.padding_0)}
                                    text={popoverData?.text}
                                    linkData={popoverData?.linkData}
                                    onLinkClick={onPopoverLinkClick}
                                />
                            }
                            onClose={closePopover}
                            clickable
                            closable
                            hoverable={false}
                            zIndex={10100}
                        >
                            <Icon
                                onClick={requestFieldHelp}
                                componentClasses={styles.icon}
                                iconColor="secondary-400"
                                icon={PriorityIcon.help}
                            />
                        </Popover>
                    ) : null}
                </Text>

                {description && !readOnly ? (
                    <Text fontWeight="normal" tag="div" skin="paragraph-1">
                        {description}
                    </Text>
                ) : null}
            </div>

            {readOnly ? (
                <Text
                    tag="div"
                    textColor="primary-700"
                    skin="paragraph-1"
                    componentClasses={styles.readOnlyValue}
                >
                    {readOnlyValue}
                </Text>
            ) : (
                isValidElement(children) &&
                cloneElement(children, {
                    name: fieldName,
                    state: Boolean(error) ? 'error' : undefined
                } as {state?: 'error'; name: string})
            )}

            {error ? (
                <Text
                    tag="div"
                    skin="caption-2"
                    textColor="validation-red"
                    componentClasses={styles.error}
                >
                    {error}
                </Text>
            ) : null}
        </div>
    );
};
